import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { DashboardEconomyTimelineEntry } from '@interfaces/dashboard/responses/dashboard-economy-changes.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { VaultFormatCurrencyPipe } from '@shared/pipes/vault-format-currency.pipe';
import { SharedModule } from '@shared/shared.module';
import {
  EconomyMeasure,
  economyTriggerLabel,
  isAutomaticEconomyTrigger,
} from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.labels';
import { isZeroAmount } from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.summary';
import { TimelineRow, buildTimelineTree, collectParentKeys } from './economy-history-timeline.tree';

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/**
 * Linha do tempo das versões da economia (card 3295; árvore no card 3323): Dia → Empresa → Versões,
 * com filtro por empresa e "só eventos". O nível de empresa some quando o filtro é uma empresa só,
 * e reprocessos manuais seguidos sem variação viram um nó próprio.
 */
@Component({
  selector: 'app-economy-history-timeline',
  standalone: true,
  imports: [CommonModule, SharedModule, VaultFormatCurrencyPipe],
  templateUrl: './economy-history-timeline.component.html',
  styleUrls: ['./economy-history-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EconomyHistoryTimelineComponent {
  private readonly dateFormatter = inject(DateFormatterService);

  readonly entries = input.required<DashboardEconomyTimelineEntry[]>();
  readonly measure = input<EconomyMeasure>('inicial');
  readonly companyNames = input<Record<string, string | null>>({});
  /** Mostra o filtro e o nível de empresa quando há mais de uma no pedido. */
  readonly showCompany = input(false);

  /** Empresa filtrada ('' = todas). */
  readonly companyFilter = signal('');
  readonly onlyEvents = signal(false);

  /**
   * O que a pessoa abriu ou fechou, por chave do nó. Os cliques mexem no mapa sem disparar
   * recálculo (a árvore já mudou o nó); trocar filtro remonta a árvore lendo o mapa.
   */
  private readonly expansionOverrides = signal(new Map<string, boolean>());

  readonly triggerLabel = economyTriggerLabel;
  readonly isAutomatic = isAutomaticEconomyTrigger;

  readonly companies = computed(() => {
    const ids = [...new Set((this.entries() ?? []).map(e => e.companyId))];
    return ids
      .map(id => ({ id, name: this.companyNames()[id] ?? '' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  readonly skipCompanyLevel = computed(
    () => !this.showCompany() || this.companies().length <= 1 || this.companyFilter() !== ''
  );

  readonly nodes = computed<TreeNode<TimelineRow>[]>(() => {
    const company = this.companyFilter();
    const onlyEvents = this.onlyEvents();
    const all = this.entries() ?? [];
    const overrides = this.expansionOverrides();
    const filtered = all.filter(
      e =>
        (!company || e.companyId === company) &&
        (!onlyEvents || !isAutomaticEconomyTrigger(e.trigger))
    );

    return buildTimelineTree(filtered, {
      value: e => this.value(e),
      delta: e => this.delta(e),
      companyName: id => this.companyNames()[id] ?? '',
      dayKey: e => this.dayKey(e),
      dayLabel: e => this.dayLabel(e.generatedAt),
      skipCompanyLevel: this.skipCompanyLevel(),
      oldestDayKey: all.length ? this.dayKey(all[all.length - 1]) : '',
      isExpanded: (key, defaultValue) => overrides.get(key) ?? defaultValue,
    });
  });

  value(entry: DashboardEconomyTimelineEntry): number {
    return this.measure() === 'inicial' ? entry.economiaPrevista : entry.economiaReal;
  }

  delta(entry: DashboardEconomyTimelineEntry): number | null {
    return this.measure() === 'inicial' ? entry.deltaPrevista : entry.deltaReal;
  }

  time(entry: DashboardEconomyTimelineEntry): string {
    return this.dateFormatter.formatDateInFormat(entry.generatedAt, 'HH:mm');
  }

  signClass(value: number | null): string {
    if (isZeroAmount(value)) return 'is-zero';
    return (value as number) > 0 ? 'is-up' : 'is-down';
  }

  /** "3 versões · 1 evento". */
  countLabel(row: TimelineRow): string {
    const versions = `${row.versionCount} ${row.versionCount === 1 ? 'versão' : 'versões'}`;
    if (!row.eventCount) return versions;
    return `${versions} · ${row.eventCount} ${row.eventCount === 1 ? 'evento' : 'eventos'}`;
  }

  /** Detalhe e autor da versão ("Contrato X · por Fulano"). */
  detail(entry: DashboardEconomyTimelineEntry): string {
    const author = entry.triggeredByName ? `por ${entry.triggeredByName}` : '';
    return [entry.triggerDetail, author].filter(Boolean).join(' · ');
  }

  onNodeToggle(event: { node: TreeNode<TimelineRow> }, expanded: boolean): void {
    if (event.node?.key) {
      this.expansionOverrides().set(event.node.key, expanded);
    }
  }

  expandAll(): void {
    this.setAllExpanded(true);
  }

  collapseAll(): void {
    this.setAllExpanded(false);
  }

  private setAllExpanded(expanded: boolean): void {
    const map = new Map<string, boolean>();
    for (const key of collectParentKeys(this.nodes())) {
      map.set(key, expanded);
    }
    // Mantém o que foi aberto/fechado em nós que o filtro atual esconde.
    for (const [key, value] of this.expansionOverrides()) {
      if (!map.has(key)) map.set(key, value);
    }
    this.expansionOverrides.set(map);
  }

  private dayKey(entry: DashboardEconomyTimelineEntry): string {
    return this.dateFormatter.formatDateInFormat(entry.generatedAt, 'yyyy-MM-dd');
  }

  /** "07/10/2026 · terça". */
  private dayLabel(generatedAt: string): string {
    const date = this.dateFormatter.parseDate(generatedAt);
    const text = this.dateFormatter.formatDateInFormat(generatedAt, 'dd/MM/yyyy');
    return date ? `${text} · ${WEEKDAYS[date.getDay()]}` : text;
  }
}
